import React, { useEffect } from 'react';

import L from 'leaflet';
import 'leaflet-control-geocoder';
import 'leaflet-measure';
import 'leaflet-draw';
import 'leaflet-side-by-side';

import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet-measure/dist/leaflet-measure.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import '@fortawesome/fontawesome-free/css/all.css';

import "utils/leafletUtils/betterWmsLayer";
import setupLeafletMap from 'utils/leafletUtils/setupLeafletMap';
import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';
import betterWmsLayer from 'utils/leafletUtils/betterWmsLayer';

function Map({ markerModeRef, tscModeRef, setTscCoords, mapRef }) {

  useEffect(() => {
    mapRef.current = setupLeafletMap();
    const disputedLayerRef = { current: null };

    // Detect user country first
    fetch("https://ipapi.co/json/") // or any reliable geo-IP API
      .then(res => res.json())
      .then(data => {
        if (data.country_code === "RS") { // Only for Serbian users
          fetch('/ne_kosovo_serbia_disputed.geojson')
            .then(r => r.json())
            .then(gj => {
              if (!mapRef.current) return; // map might have unmounted

              // Create the layer
              const layer = L.geoJSON(gj, {
                style: {
                  color: '#ff5d5d63',
                  weight: 4,
                  dashArray: '2,10',
                  interactive: false
                }
              }).addTo(mapRef.current);

              // Assign a custom identifier AFTER creation
              layer.layerId = 'disputed-border';

              // Save to ref
              disputedLayerRef.current = layer;
            })
            .catch(err => console.error("Failed to load disputed border GeoJSON:", err));
        }
      })
      .catch(err => console.error("Failed to detect user country:", err));

    const showPopUp = (e) => {
      // Necessary because if the zoom is greater than 15 the mapserver returns empty string instead of the value
      let waitingTime = 0;

      if (mapRef.current.getZoom() > 15) {
        mapRef.current.setZoom(15);
        waitingTime = 100;
      }

      // Wait 100 ms to make sure the map zoom has been updated so the getValue method returns a valid value
      setTimeout(async () => {
        const layerInfoArray = [];

        // Array to store all promises
        const promises = [];

        // Iterate over each layer in the map
        mapRef.current.eachLayer((layer) => {
          if (layer instanceof betterWmsLayer) {
            const layerName = LayersAndResolutions.layers.find((auxLayer) => auxLayer.shortName === layer.options.provider);
            const layerInfo = {
              name: layerName,
              date: `${layer.options.day}/${layer.options.month}/${layer.options.year}`,
              value: null
            };

            // Add the promise returned by getValue to the promises array
            const promise = layer.getValue(e, mapRef)
              .then(result => {
                layerInfo.value = result[1]; // The value is at index 1
                layerInfoArray.push(layerInfo);
              })
              .catch(error => {
                layerInfo.value = 'Point out of extent or no data was returned';
                layerInfoArray.push(layerInfo);
              });

            promises.push(promise);
          }
        });

        // Wait for all promises to resolve
        Promise.all(promises).then(() => {
          const coordContent = `${e.latlng.lat.toFixed(4)}, ${e.latlng.lng.toFixed(4)}`;
          const isSingleLayer = layerInfoArray.length === 1;

          // common date if all layers have the same date
          let commonDate = null;
          if (layerInfoArray.length > 0) {
            const firstDate = layerInfoArray[0].date;
            const allSameDate = layerInfoArray.every(info => info.date === firstDate);
            if (allSameDate) {
              commonDate = firstDate;
            }
          }

          let popupContent = '';

          if (isSingleLayer) {
            // Single layer format
            popupContent = `
          <div>
            <strong>${layerInfoArray[0].date}</strong><br>
            <strong>Coords:</strong> ${coordContent}<br><br>
            <strong>${layerInfoArray[0].name.longName}</strong><br>
            <strong>Value:</strong> ${layerInfoArray[0].value}
          </div>
        `;
          } else {
            // Multiple layers format
            popupContent = `
          <div>
            ${commonDate ? `<strong>${commonDate}</strong><br>` : ''}
            <strong>Coords:</strong> ${coordContent}<br><br>
        `;

            layerInfoArray.forEach(info => {
              popupContent += `
            <strong>${info.name.longName}</strong><br>
            ${!commonDate ? `<strong>Date:</strong> ${info.date}<br>` : ''}
            <strong>Value:</strong> ${info.value}<br><br>
          `;
            });

            popupContent += '</div>';
          }

          // Show the popup with the structured content
          L.popup({ maxWidth: 825 })
            .setLatLng(e.latlng)
            .setContent(popupContent)
            .openOn(mapRef.current);
        });
      }, waitingTime);
    };

    const handleMarkerCreation = (event) => {
      if (markerModeRef.current) {
        showPopUp(event);
      }
    };

    const handleTSC = (e) => { // Function that gets the value for a marker when it's set.
      if (tscModeRef.current) {
        setTscCoords({ lat: e.latlng.lat, lng: e.latlng.lng });

        const coordinatesString = `Times series chart coordinates <br> ${e.latlng.lat.toFixed(4)} , ${e.latlng.lng.toFixed(4)}`;

        L.popup({ maxWidth: 800 })
          .setLatLng(e.latlng)
          .setContent(coordinatesString)
          .openOn(mapRef.current);
      }
    };

    function combinedClickHandler(e) {
      handleMarkerCreation(e);
      handleTSC(e);
    }

    mapRef.current.on('click', combinedClickHandler);

    // Cleanup on unmount.
    return () => {
      mapRef.current.remove();
      mapRef.current.off('mousemove'); // Cleanup
      mapRef.current.off('click', combinedClickHandler);
      mapRef.current = null;
    };
  }, []);

  return (

    <div id="map" style={{ width: '100%', height: '100%' }}>
      <div id="coordinates">
        Lat: 0, Lng: 0
      </div>
    </div>

  );
}

export default Map;